import { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './store/i18n'

import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/lib/integration/react'
import { store, persistor } from './store'
import '@pulsawork/ui/styles.css'
import './index.css'
import { ThemeProvider } from '@pulsawork/ui'

import PublicRoute from './components/routes/PublicRoute'
import PrivateRoute from './components/routes/PrivateRoute'

const ErrorPage = lazy(() => import('./ErrorPage'))
const Login = lazy(() => import('./login/Login'))
const Dashboard = lazy(() => import('./dashboard/Dashboard'))
const Register = lazy(() => import('./register/Register'))
const Onboarding = lazy(() => import('./onboarding/Onboarding'))
const ResetPassword = lazy(() => import('./password-reset/ResetPassword'))
const ResetPasswordConfirm = lazy(() => import('./reset-confirm/ResetPasswordConfirm'))
const Activation = lazy(() => import('./activation/Activation'))

const LoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
)

const router = createBrowserRouter([
    {
        path: '/*',
        element: (
            <PrivateRoute>
                <Suspense fallback={<LoadingSpinner />}>
                    <Dashboard />
                </Suspense>
            </PrivateRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/login',
        element: (
            <PublicRoute restricted={true}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Login />
                </Suspense>
            </PublicRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/register',
        element: (
            <PublicRoute restricted={false}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Register />
                </Suspense>
            </PublicRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/password-reset',
        element: (
            <PublicRoute restricted={false}>
                <Suspense fallback={<LoadingSpinner />}>
                    <ResetPassword />
                </Suspense>
            </PublicRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/password/reset/confirm/:uid/:token',
        element: (
            <PublicRoute restricted={false}>
                <Suspense fallback={<LoadingSpinner />}>
                    <ResetPasswordConfirm />
                </Suspense>
            </PublicRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/activate/:uid/:token',
        element: (
            <PublicRoute restricted={false}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Activation />
                </Suspense>
            </PublicRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/onboarding',
        element: (
            <PrivateRoute>
                <Suspense fallback={<LoadingSpinner />}>
                    <Onboarding />
                </Suspense>
            </PrivateRoute>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/dashboard/*',
        element: (
            <PrivateRoute>
                <Suspense fallback={<LoadingSpinner />}>
                    <Dashboard />
                </Suspense>
            </PrivateRoute>
        ),
        errorElement: <ErrorPage />
    },
])

createRoot(document.getElementById('root')).render(
    <ThemeProvider>
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <RouterProvider router={router} />
            </PersistGate>
        </Provider>
    </ThemeProvider>
)
